// This modal opens when the user clicks on "Add Application"

import React, { useContext, useEffect, useState } from 'react';
import Select from 'react-select';
import { CYCLE_YEAR, EARLY_CAREER_GRAD_YEAR } from '../../config/recruitingCycle';
import { ApplicationTrackerContext } from '../../contexts/ApplicationTrackerContext';
import { useAuth } from '../../contexts/AuthContext';
import { GlobalContext } from '../../contexts/GlobalContext';
import axios from '../../utils/axiosConfig';
import CompanySearchModal from './CompanySearchModal';

const AddApplicationModal = ({
  showOverlay = true,
  onClose, //Used for the ApplicationTaskModal to combine this closing function with the parent's closing function
  prefilledRole = null, // For JobBoard integration - contains the public role data
}) => {
  const { setAddApplicationModalOpen, setRefreshKey, setCurrentApps, initialValues } =
    useContext(ApplicationTrackerContext);
  const { userProfile, onboardingInfo, fetchQuest, questPlan, showToastMessage } =
    useContext(GlobalContext);
  const { user } = useAuth();

  // Determine if we're in "use existing role" mode (from JobBoard) or "create new" mode
  const isUsingExistingRole = prefilledRole !== null;

  const [currentStep, setCurrentStep] = useState(1); // 1: Basic Info, 2: Role Details, 3: Review, 4: Admin Options
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAdminView, setIsAdminView] = useState(false); // Controls whether admin features are shown
  const [makePublic, setMakePublic] = useState(false);
  const [makeRecommended, setMakeRecommended] = useState(false);
  const [applicationInfo, setApplicationInfo] = useState({
    companyName: initialValues?.companyName || prefilledRole?.company?.company_name || '',
    positionName: initialValues?.positionName || prefilledRole?.name || '',
    status: '',
    applicationLink: initialValues?.applicationLink || prefilledRole?.link || '',
    date: '',
  });

  const [roleDetails, setRoleDetails] = useState({
    description: prefilledRole?.description || '',
    skills: prefilledRole?.skills || [],
    location: prefilledRole?.location || '',
    categories: prefilledRole?.categories || [],
    programming_languages: prefilledRole?.programming_languages || [],
    frameworks_and_tools: prefilledRole?.frameworks_and_tools || [],
    job_focus: prefilledRole?.job_focus || 'Other',
    visa_status: prefilledRole?.visa_status || 'Not Specified',
    min_education_level: prefilledRole?.min_education_level || 'Bachelor',
    min_graduation_year: prefilledRole?.min_graduation_year || null,
    max_graduation_year: prefilledRole?.max_graduation_year || null,
    requires_security_clearance: prefilledRole?.requires_security_clearance || false,
    work_location: prefilledRole?.work_location || 'On-site',
    internship_season: prefilledRole?.internship_season || 'Summer',
    duration_weeks: prefilledRole?.duration_weeks || null,
    housing_status: prefilledRole?.housing_status || 'Not Specified',
    required_gpa: prefilledRole?.required_gpa || null,
    application_deadline: prefilledRole?.application_deadline || null,
    company_size: prefilledRole?.company_size || null,
  });

  const [useExistingRole, setUseExistingRole] = useState(isUsingExistingRole);
  const [hasModifiedValues, setHasModifiedValues] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Company search modal state
  const [companySearchModalOpen, setCompanySearchModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);

  // Array management state
  const [newSkill, setNewSkill] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [newProgrammingLanguage, setNewProgrammingLanguage] = useState('');
  const [newFramework, setNewFramework] = useState('');

  // Check admin status on component mount
  useEffect(() => {
    console.log('Checking admin status');
    const checkAdminStatus = async () => {
      if (!userProfile?.user_id) return;

      try {
        const response = await axios.get(`/api/v1/user/${userProfile.user_id}/is-admin`);
        setIsAdmin(response.data.is_admin);
      } catch (error) {
        console.error('Error checking admin status:', error);
      }
    };

    checkAdminStatus();
  }, [userProfile]);

  // Track if user has modified the pre-filled values
  useEffect(() => {
    if (prefilledRole) {
      const hasChanges =
        applicationInfo.companyName !== prefilledRole.company?.company_name ||
        applicationInfo.positionName !== prefilledRole.name ||
        applicationInfo.applicationLink !== (prefilledRole.link || '');
      setHasModifiedValues(hasChanges);
      setUseExistingRole(!hasChanges); // If they modified values, default to creating new role
    }
  }, [applicationInfo, prefilledRole]);

  // Initialize selected company from prefilled data
  useEffect(() => {
    if (prefilledRole?.company) {
      setSelectedCompany({
        company_id: prefilledRole.company.company_id,
        company_name: prefilledRole.company.company_name,
        logo_url: prefilledRole.company.logo_url,
        linkedin_id: prefilledRole.company.linkedin_id,
        isExisting: true,
      });
    }
  }, [prefilledRole]);

  const addArrayItem = (field, newItem, setNewItem) => {
    if (newItem.trim()) {
      setRoleDetails((prev) => ({
        ...prev,
        [field]: [...prev[field], newItem.trim()],
      }));
      setNewItem('');
    }
  };

  const removeArrayItem = (field, index) => {
    setRoleDetails((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const handleCompanySelect = (company) => {
    setSelectedCompany(company);
    setApplicationInfo((prev) => ({
      ...prev,
      companyName: company.company_name,
    }));
    setCompanySearchModalOpen(false);
  };

  const addApplication = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      let applicationRequest;
      let createdRoleId = null;

      if (useExistingRole && prefilledRole && !hasModifiedValues) {
        // Use the existing public role
        applicationRequest = {
          application: {
            user_id: parseInt(localStorage.getItem('userID')),
          },
          create_new_role: false,
          existing_role_id: prefilledRole.role_id,
        };
      } else {
        // Create new role (and possibly new company)
        let companyId = null;
        let createNewCompany = false;
        let newCompanyData = null;
        let existingCompanyId = null;

        if (selectedCompany && selectedCompany.isExisting) {
          // Use existing company
          createNewCompany = false;
          existingCompanyId = selectedCompany.company_id;
          companyId = selectedCompany.company_id;
        } else if (selectedCompany) {
          // Create new company - company_id will be null since it doesn't exist yet
          createNewCompany = true;
          companyId = null;
          newCompanyData = {
            company: {
              company_name: selectedCompany.company_name,
              logo_url: selectedCompany.logo_url || null,
              linkedin_id: selectedCompany.linkedin_id || null,
            },
          };
        } else {
          // Fallback to company name input (shouldn't happen with new flow)
          createNewCompany = true;
          companyId = null;
          newCompanyData = {
            company: {
              company_name: applicationInfo.companyName,
              logo_url: null,
              linkedin_id: null,
            },
          };
        }

        applicationRequest = {
          application: {
            user_id: parseInt(localStorage.getItem('userID')),
          },
          create_new_role: true,
          new_role: {
            role: {
              name: applicationInfo.positionName,
              company_id: companyId,
              description: roleDetails.description,
              skills: roleDetails.skills,
              location: roleDetails.location,
              link: applicationInfo.applicationLink,
              categories: roleDetails.categories,
              post_date: new Date().toISOString().split('T')[0],
              is_active: true,
              programming_languages: roleDetails.programming_languages,
              frameworks_and_tools: roleDetails.frameworks_and_tools,
              job_focus: roleDetails.job_focus,
              visa_status: roleDetails.visa_status,
              min_education_level: roleDetails.min_education_level,
              min_graduation_year: roleDetails.min_graduation_year,
              max_graduation_year: roleDetails.max_graduation_year,
              requires_security_clearance: roleDetails.requires_security_clearance,
              work_location: roleDetails.work_location,
              internship_season: roleDetails.internship_season,
              duration_weeks: roleDetails.duration_weeks,
              housing_status: roleDetails.housing_status,
              required_gpa: roleDetails.required_gpa,
              application_deadline: roleDetails.application_deadline,
              company_size: roleDetails.company_size,
            },
            create_new_company: createNewCompany,
          },
        };

        if (existingCompanyId) {
          applicationRequest.new_role.existing_company_id = existingCompanyId;
        }

        if (newCompanyData) {
          applicationRequest.new_role.new_company = newCompanyData;
        }
      }

      // Create the application
      const response = await axios.post('/api/v1/application/create', applicationRequest);
      const data = response.data;
      console.log('Application created:', data);

      if (!data.application_id) {
        throw new Error('No application ID returned from create application');
      }

      // If this is a new role and admin wants to make it public, use the admin endpoint
      if (isAdmin && isAdminView && makePublic) {
        console.log('Making role public');
        // Get the role_id from the response
        const roleId = data.role_id;
        if (!roleId) {
          console.warn('No role_id in response, cannot make role public');
        } else {
          try {
            await axios.post(`/api/v1/admin/role/${roleId}/make-public`);
            console.log('Successfully made role public');
          } catch (error) {
            console.warn(
              'Error making role public:',
              error.response?.data?.detail || error.message
            );
          }
        }
      }

      // If this is a new role and admin wants to make it recommended, use the admin endpoint
      if (isAdmin && isAdminView && makeRecommended) {
        console.log('Making role recommended');
        // Get the role_id from the response
        const roleId = data.role_id;
        if (!roleId) {
          console.warn('No role_id in response, cannot make role recommended');
        } else {
          try {
            await axios.post(`/api/v1/admin/role/${roleId}/make-recommended`);
            console.log('Successfully made role recommended');
          } catch (error) {
            console.warn(
              'Error making role recommended:',
              error.response?.data?.detail || error.message
            );
          }
        }
      }

      // Create application status
      const applicationStatusData = {
        application_status: {
          application_id: data.application_id,
          status: applicationInfo.status,
          status_date: new Date().toISOString(),
        },
      };

      await axios.post('/api/v1/application/status/create', applicationStatusData);

      // Add to quest if applicable
      if (
        questPlan &&
        questPlan.quest_id &&
        applicationInfo.status !== 'Application Started' &&
        applicationInfo.status !== 'Application Desired'
      ) {
        try {
          // Get user timezone for calendar date context
          const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

          const questUpdateResponse = await axios.post(
            `/api/v1/quest/${questPlan.quest_id}/add_application_by_date`,
            {
              company_name: applicationInfo.companyName,
              application_date: new Date().toISOString(),
            },
            {
              headers: {
                'X-User-Timezone': userTimezone,
              },
            }
          );

          showToastMessage(
            `Your application to ${applicationInfo.companyName} was added to your quest!`
          );
          fetchQuest();
        } catch (questError) {
          console.warn(
            'Failed to update quest, but application was created successfully:',
            questError
          );
        }
      }

      setRefreshKey((prev) => prev + 1);
      setAddApplicationModalOpen(false);
      if (onClose) onClose();
    } catch (error) {
      console.error('Error creating application:', error);
      showToastMessage(
        `Failed to create application: ${error.response?.data?.detail || error.message}`,
        'error'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setAddApplicationModalOpen(false);
    if (onClose) onClose();
  };

  const handleInputChange = (field, value) => {
    setApplicationInfo((prev) => ({ ...prev, [field]: value }));
  };

  const handleRoleDetailChange = (field, value) => {
    setRoleDetails((prev) => ({ ...prev, [field]: value }));
  };

  const getStatusColor = (status) => {
    const colors = {
      'Application Desired': 'bg-purple-100 text-purple-800 border border-purple-200',
      'Application Started': 'bg-gray-100 text-gray-800 border border-gray-200',
      'Application Submitted': 'bg-blue-100 text-blue-800 border border-blue-200',
      'Online Assessment': 'bg-indigo-100 text-indigo-800 border border-indigo-200',
      'Live Interviewing': 'bg-yellow-100 text-yellow-800 border border-yellow-200',
      'Offer Received': 'bg-green-100 text-green-800 border border-green-200',
      Rejected: 'bg-red-100 text-red-800 border border-red-200',
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border border-gray-200';
  };

  const options = [
    { value: 'Application Desired', label: 'Application Desired' },
    { value: 'Application Started', label: 'Application Started' },
    { value: 'Application Submitted', label: 'Application Submitted' },
    { value: 'Live Interviewing', label: 'Live Interviewing' },
    { value: 'Offer Received', label: 'Offer Received' },
    { value: 'Rejected', label: 'Rejected' },
    { value: 'Online Assessment', label: 'Online Assessment' },
  ];

  const nextStep = () => {
    if (currentStep === 1 && shouldSkipRoleDetails()) {
      setCurrentStep(3); // Skip step 2 and go directly to review
    } else if (currentStep < (isAdmin && isAdminView ? 4 : 3)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep === 3 && shouldSkipRoleDetails()) {
      setCurrentStep(1); // Skip step 2 and go back to step 1
    } else if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const shouldSkipRoleDetails = () => {
    return prefilledRole && useExistingRole && !hasModifiedValues;
  };

  const canProceedToNext = () => {
    if (currentStep === 1) {
      return applicationInfo.companyName && applicationInfo.positionName && applicationInfo.status;
    }
    return true;
  };

  const getStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            {/* Option selector when coming from JobBoard */}
            {prefilledRole && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="text-sm font-medium text-blue-800 mb-3">Role Source</h4>
                <div className="space-y-3">
                  <label className="flex items-start">
                    <input
                      type="radio"
                      name="roleSource"
                      checked={useExistingRole && !hasModifiedValues}
                      onChange={() => {
                        setUseExistingRole(true);
                        // Reset to original values
                        setApplicationInfo({
                          companyName: prefilledRole.company?.company_name || '',
                          positionName: prefilledRole.name || '',
                          status: applicationInfo.status,
                          applicationLink: prefilledRole.link || '',
                          date: applicationInfo.date,
                        });
                      }}
                      className="shrink-0 mt-0.5 border-gray-200 rounded-sm text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-blue-700 ml-2">
                      Use existing public role: <strong>{prefilledRole.name}</strong> at{' '}
                      <strong>{prefilledRole.company?.company_name}</strong>
                    </span>
                  </label>
                  <label className="flex items-start">
                    <input
                      type="radio"
                      name="roleSource"
                      checked={!useExistingRole || hasModifiedValues}
                      onChange={() => setUseExistingRole(false)}
                      className="shrink-0 mt-0.5 border-gray-200 rounded-sm text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-blue-700 ml-2">
                      Create new role with custom details
                    </span>
                  </label>
                </div>
                {hasModifiedValues && (
                  <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs text-amber-700 flex items-center">
                      <svg
                        className="size-4 mr-1"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="m21 16-4 4-4-4" />
                        <path d="M17 20V4" />
                        <path d="m3 8 4-4 4 4" />
                        <path d="M7 4v16" />
                      </svg>
                      Values have been modified - will create new role
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="grid gap-4 lg:gap-6">
              {/* Company selection */}
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Company <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => setCompanySearchModalOpen(true)}
                  disabled={useExistingRole && !hasModifiedValues && prefilledRole}
                  className="w-full py-2.5 sm:py-3 px-4 text-left border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none transition-colors"
                >
                  {selectedCompany ? (
                    <div className="flex items-center space-x-3">
                      <img
                        src={
                          selectedCompany.logo_url ||
                          `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedCompany.company_name)}&background=6366f1&color=fff&bold=true`
                        }
                        alt={`${selectedCompany.company_name} logo`}
                        className="size-8 object-contain bg-gray-100 rounded-lg"
                      />
                      <span className="text-gray-900 font-medium">
                        {selectedCompany.company_name}
                      </span>
                    </div>
                  ) : (
                    <span className="text-gray-500">Select a company...</span>
                  )}
                </button>
              </div>

              <div>
                <label
                  htmlFor="position-name"
                  className="block mb-2 text-sm font-medium text-gray-700"
                >
                  Position name <span className="text-red-500">*</span>
                </label>
                <input
                  id="position-name"
                  value={applicationInfo.positionName}
                  onChange={(e) => handleInputChange('positionName', e.target.value)}
                  type="text"
                  className="py-2.5 sm:py-3 px-4 block w-full border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                  placeholder="e.g., Software Engineering Intern"
                  disabled={useExistingRole && !hasModifiedValues && prefilledRole}
                />
              </div>

              <div>
                <label
                  htmlFor="application-link"
                  className="block mb-2 text-sm font-medium text-gray-700"
                >
                  Application link (optional)
                </label>
                <input
                  id="application-link"
                  value={applicationInfo.applicationLink}
                  onChange={(e) => handleInputChange('applicationLink', e.target.value)}
                  type="url"
                  className="py-2.5 sm:py-3 px-4 block w-full border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                  placeholder="https://company.com/careers/123"
                  disabled={useExistingRole && !hasModifiedValues && prefilledRole}
                />
              </div>

              <div>
                <label htmlFor="status" className="block mb-2 text-sm font-medium text-gray-700">
                  Status <span className="text-red-500">*</span>
                </label>
                <Select
                  options={options}
                  onChange={(option) => handleInputChange('status', option.value)}
                  placeholder="Select application status..."
                  className="text-sm"
                  classNamePrefix="select"
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 10000 }),
                    control: (base) => ({
                      ...base,
                      border: '1px solid #d1d5db',
                      borderRadius: '0.5rem',
                      minHeight: '42px',
                      '&:hover': {
                        borderColor: '#3b82f6',
                      },
                    }),
                    menuList: (base) => ({
                      ...base,
                      maxHeight: '200px',
                      overflowY: 'auto',
                      borderRadius: '0.5rem',
                    }),
                    option: (base, { data, isSelected }) => ({
                      ...base,
                      backgroundColor: isSelected ? '#3b82f6' : 'white',
                      color: isSelected ? 'white' : '#374151',
                      '&:hover': {
                        backgroundColor: isSelected ? '#3b82f6' : '#f3f4f6',
                      },
                      padding: '8px 12px',
                    }),
                    singleValue: (base, { data }) => {
                      if (!data?.value) return base;
                      const colorClasses = getStatusColor(data.value);
                      return {
                        ...base,
                        display: 'flex',
                        alignItems: 'center',
                      };
                    },
                  }}
                  formatOptionLabel={(option) => (
                    <span
                      className={`inline-block px-2 py-1 rounded-sm text-xs font-medium ${getStatusColor(option.value)}`}
                    >
                      {option.label}
                    </span>
                  )}
                  value={options.find((opt) => opt.value === applicationInfo.status)}
                />
              </div>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-8">
            {/* Basic Role Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Role Details</h3>
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="role-description"
                    className="block mb-2 text-sm font-medium text-gray-700"
                  >
                    Description
                  </label>
                  <textarea
                    id="role-description"
                    rows={4}
                    className="py-2.5 sm:py-3 px-4 block w-full border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                    value={roleDetails.description}
                    onChange={(e) => handleRoleDetailChange('description', e.target.value)}
                    placeholder="Describe the role responsibilities, requirements, and expectations..."
                  />
                </div>

                <div>
                  <label
                    htmlFor="role-location"
                    className="block mb-2 text-sm font-medium text-gray-700"
                  >
                    Location
                  </label>
                  <input
                    type="text"
                    id="role-location"
                    className="py-2.5 sm:py-3 px-4 block w-full border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                    value={roleDetails.location}
                    onChange={(e) => handleRoleDetailChange('location', e.target.value)}
                    placeholder="e.g., San Francisco, CA"
                  />
                </div>
              </div>
            </div>

            {/* Skills & Technologies */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Skills & Technologies</h3>
              <div className="space-y-6">
                {/* Skills */}
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Required Skills
                  </label>
                  <div className="flex gap-x-2 mb-3">
                    <input
                      type="text"
                      className="py-2.5 px-4 block w-full border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500"
                      value={newSkill}
                      onChange={(e) => setNewSkill(e.target.value)}
                      placeholder="e.g., React, Problem Solving, Communication"
                      onKeyPress={(e) =>
                        e.key === 'Enter' &&
                        e.preventDefault() &&
                        addArrayItem('skills', newSkill, setNewSkill)
                      }
                    />
                    <button
                      type="button"
                      className="py-2.5 px-4 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg border border-transparent bg-blue-600 text-white hover:bg-blue-700 focus:outline-hidden focus:bg-blue-700 disabled:opacity-50 disabled:pointer-events-none"
                      onClick={() => addArrayItem('skills', newSkill, setNewSkill)}
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {roleDetails.skills.map((skill, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-x-1.5 py-1.5 px-3 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {skill}
                        <button
                          type="button"
                          onClick={() => removeArrayItem('skills', index)}
                          className="inline-flex items-center justify-center size-4 rounded-full text-blue-800 hover:bg-blue-200"
                        >
                          <svg
                            className="shrink-0 size-3"
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="m18 6-12 12" />
                            <path d="m6 6 12 12" />
                          </svg>
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                {/* Programming Languages */}
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Programming Languages
                  </label>
                  <div className="flex gap-x-2 mb-3">
                    <input
                      type="text"
                      className="py-2.5 px-4 block w-full border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500"
                      value={newProgrammingLanguage}
                      onChange={(e) => setNewProgrammingLanguage(e.target.value)}
                      placeholder="e.g., Python, JavaScript, Java"
                      onKeyPress={(e) =>
                        e.key === 'Enter' &&
                        e.preventDefault() &&
                        addArrayItem(
                          'programming_languages',
                          newProgrammingLanguage,
                          setNewProgrammingLanguage
                        )
                      }
                    />
                    <button
                      type="button"
                      className="py-2.5 px-4 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg border border-transparent bg-blue-600 text-white hover:bg-blue-700 focus:outline-hidden focus:bg-blue-700 disabled:opacity-50 disabled:pointer-events-none"
                      onClick={() =>
                        addArrayItem(
                          'programming_languages',
                          newProgrammingLanguage,
                          setNewProgrammingLanguage
                        )
                      }
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {roleDetails.programming_languages.map((lang, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-x-1.5 py-1.5 px-3 rounded-full text-xs font-medium bg-green-100 text-green-800"
                      >
                        {lang}
                        <button
                          type="button"
                          onClick={() => removeArrayItem('programming_languages', index)}
                          className="inline-flex items-center justify-center size-4 rounded-full text-green-800 hover:bg-green-200"
                        >
                          <svg
                            className="shrink-0 size-3"
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="m18 6-12 12" />
                            <path d="m6 6 12 12" />
                          </svg>
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Role Requirements */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Role Requirements</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                {/* Job Focus */}
                <div>
                  <label
                    htmlFor="job-focus"
                    className="block mb-2 text-sm font-medium text-gray-700"
                  >
                    Job Focus
                  </label>
                  <select
                    id="job-focus"
                    className="py-2.5 px-4 pe-9 block w-full border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                    value={roleDetails.job_focus}
                    onChange={(e) => handleRoleDetailChange('job_focus', e.target.value)}
                    style={{ zIndex: 'auto' }}
                  >
                    <option value="Frontend">Frontend</option>
                    <option value="Backend">Backend</option>
                    <option value="Full Stack">Full Stack</option>
                    <option value="Mobile">Mobile</option>
                    <option value="DevOps">DevOps</option>
                    <option value="ML/AI">ML/AI</option>
                    <option value="Data Engineering">Data Engineering</option>
                    <option value="Security">Security</option>
                    <option value="Embedded Systems">Embedded Systems</option>
                    <option value="Cloud">Cloud</option>
                  </select>
                </div>

                {/* Work Location */}
                <div>
                  <label
                    htmlFor="work-location"
                    className="block mb-2 text-sm font-medium text-gray-700"
                  >
                    Work Location
                  </label>
                  <select
                    id="work-location"
                    className="py-2.5 px-4 pe-9 block w-full border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                    value={roleDetails.work_location}
                    onChange={(e) => handleRoleDetailChange('work_location', e.target.value)}
                    style={{ zIndex: 'auto' }}
                  >
                    <option value="On-site">On-site</option>
                    <option value="Hybrid">Hybrid</option>
                    <option value="Remote">Remote</option>
                    <option value="Flexible">Flexible</option>
                  </select>
                </div>

                {/* Internship Season */}
                <div>
                  <label
                    htmlFor="internship-season"
                    className="block mb-2 text-sm font-medium text-gray-700"
                  >
                    Internship Season
                  </label>
                  <select
                    id="internship-season"
                    className="py-2.5 px-4 pe-9 block w-full border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                    value={roleDetails.internship_season}
                    onChange={(e) => handleRoleDetailChange('internship_season', e.target.value)}
                    style={{ zIndex: 'auto' }}
                  >
                    <option value="Summer">Summer</option>
                    <option value="Fall">Fall</option>
                    <option value="Winter">Winter</option>
                    <option value="Spring">Spring</option>
                    <option value="Year-round">Year-round</option>
                  </select>
                </div>

                {/* Duration */}
                <div>
                  <label
                    htmlFor="duration-weeks"
                    className="block mb-2 text-sm font-medium text-gray-700"
                  >
                    Duration (weeks)
                  </label>
                  <input
                    type="number"
                    id="duration-weeks"
                    className="py-2.5 px-4 block w-full border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                    value={roleDetails.duration_weeks || ''}
                    onChange={(e) =>
                      handleRoleDetailChange(
                        'duration_weeks',
                        e.target.value ? parseInt(e.target.value) : null
                      )
                    }
                    placeholder="12"
                  />
                </div>

                {/* Required GPA */}
                <div>
                  <label
                    htmlFor="required-gpa"
                    className="block mb-2 text-sm font-medium text-gray-700"
                  >
                    Required GPA
                  </label>
                  <input
                    type="number"
                    id="required-gpa"
                    step="0.1"
                    min="0"
                    max="4.0"
                    className="py-2.5 px-4 block w-full border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                    value={roleDetails.required_gpa || ''}
                    onChange={(e) =>
                      handleRoleDetailChange(
                        'required_gpa',
                        e.target.value ? parseFloat(e.target.value) : null
                      )
                    }
                    placeholder="3.0"
                  />
                </div>
              </div>
            </div>
          </div>
        );
      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Review Application Details
              </h3>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Company</dt>
                    <dd className="text-sm text-gray-900">{applicationInfo.companyName}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Position</dt>
                    <dd className="text-sm text-gray-900">{applicationInfo.positionName}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Status</dt>
                    <dd className="text-sm text-gray-900">{applicationInfo.status}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Application Link</dt>
                    <dd className="text-sm text-gray-900">
                      {applicationInfo.applicationLink || 'Not provided'}
                    </dd>
                  </div>
                </div>

                {roleDetails.skills.length > 0 && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500 mb-2">Skills</dt>
                    <dd className="flex flex-wrap gap-1">
                      {roleDetails.skills.map((skill, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {skill}
                        </span>
                      ))}
                    </dd>
                  </div>
                )}

                {roleDetails.description && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Description</dt>
                    <dd className="text-sm text-gray-900 mt-1">{roleDetails.description}</dd>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      case 4:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Admin Options</h3>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-6">
                {/* Make Public Option */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="make-public"
                    checked={makePublic}
                    onChange={(e) => setMakePublic(e.target.checked)}
                    className="size-4 text-blue-600 border-gray-300 rounded-sm focus:ring-blue-500"
                  />
                  <label htmlFor="make-public" className="ml-2 block text-sm text-gray-900">
                    Make this role public and vetted by me
                  </label>
                </div>

                {/* Make Recommended Option */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="make-recommended"
                    checked={makeRecommended}
                    onChange={(e) => setMakeRecommended(e.target.checked)}
                    className="size-4 text-blue-600 border-gray-300 rounded-sm focus:ring-blue-500"
                  />
                  <label htmlFor="make-recommended" className="ml-2 block text-sm text-gray-900">
                    Make this role recommended
                  </label>
                </div>

                {/* Graduation Year Range */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="min-graduation-year"
                      className="block mb-2 text-sm font-medium text-gray-700"
                    >
                      Min Graduation Year
                    </label>
                    <input
                      type="number"
                      id="min-graduation-year"
                      className="py-2.5 px-4 block w-full border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500"
                      value={roleDetails.min_graduation_year || ''}
                      onChange={(e) =>
                        handleRoleDetailChange(
                          'min_graduation_year',
                          e.target.value ? parseInt(e.target.value) : null
                        )
                      }
                      placeholder={`${CYCLE_YEAR}`}
                      min="2000"
                      max="2100"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="max-graduation-year"
                      className="block mb-2 text-sm font-medium text-gray-700"
                    >
                      Max Graduation Year
                    </label>
                    <input
                      type="number"
                      id="max-graduation-year"
                      className="py-2.5 px-4 block w-full border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500"
                      value={roleDetails.max_graduation_year || ''}
                      onChange={(e) =>
                        handleRoleDetailChange(
                          'max_graduation_year',
                          e.target.value ? parseInt(e.target.value) : null
                        )
                      }
                      placeholder={`${EARLY_CAREER_GRAD_YEAR}`}
                      min="2000"
                      max="2100"
                    />
                  </div>
                </div>

                {/* Company Size */}
                <div>
                  <label
                    htmlFor="company-size"
                    className="block mb-2 text-sm font-medium text-gray-700"
                  >
                    Company Size
                  </label>
                  <select
                    id="company-size"
                    className="py-2.5 px-4 pe-9 block w-full border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500"
                    value={roleDetails.company_size || ''}
                    onChange={(e) => handleRoleDetailChange('company_size', e.target.value)}
                  >
                    <option value="">Select company size...</option>
                    <option value="1-10">1-10 employees</option>
                    <option value="11-50">11-50 employees</option>
                    <option value="51-200">51-200 employees</option>
                    <option value="201-500">201-500 employees</option>
                    <option value="501-1000">501-1000 employees</option>
                    <option value="1001-5000">1001-5000 employees</option>
                    <option value="5001-10000">5001-10000 employees</option>
                    <option value="10001+">10001+ employees</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const modalContent = (
    <div className="relative transform overflow-hidden rounded-xl bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl">
      {/* Header */}
      <div className="px-4 py-6 sm:px-6 lg:px-8 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                {prefilledRole ? 'Track Application' : 'Add New Application'}
                {isAdmin && isAdminView && ' (Admin)'}
              </h1>
              <p className="mt-1 text-sm text-gray-600">
                {prefilledRole
                  ? 'Add this role to your application tracker'
                  : 'Create a new internship application with detailed role information'}
              </p>
            </div>
            {isAdmin && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  role="switch"
                  aria-checked={isAdminView}
                  onClick={() => setIsAdminView(!isAdminView)}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 ${
                    isAdminView ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${
                      isAdminView ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
                <span className="text-sm text-gray-600">Admin View</span>
              </div>
            )}
          </div>
          <button
            onClick={handleClose}
            type="button"
            className="inline-flex justify-center items-center size-8 text-sm font-semibold rounded-lg border border-transparent text-gray-800 hover:bg-gray-100 disabled:opacity-50 disabled:pointer-events-none"
          >
            <span className="sr-only">Close</span>
            <svg
              className="shrink-0 size-4"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m18 6-12 12" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>

        {/* Progress Steps */}
        <div className="mt-6">
          <nav aria-label="Progress">
            <ol className={`grid ${isAdmin && isAdminView ? 'grid-cols-4' : 'grid-cols-3'} gap-8`}>
              {[
                { id: 1, name: 'Basic Info', description: 'Company and position details' },
                { id: 2, name: 'Role Details', description: 'Skills and requirements' },
                { id: 3, name: 'Review', description: 'Confirm and submit' },
                ...(isAdmin && isAdminView
                  ? [{ id: 4, name: 'Admin', description: 'Admin options' }]
                  : []),
              ].map((step, stepIdx) => {
                const isStepSkipped = step.id === 2 && shouldSkipRoleDetails();
                const stepNumber = isStepSkipped ? '✓' : step.id;
                const isActive = currentStep >= step.id && !isStepSkipped;
                const isCompleted = currentStep > step.id || (isStepSkipped && currentStep >= 3);

                return (
                  <li key={step.id} className="flex items-center justify-center">
                    <div className="flex flex-col items-center">
                      <div
                        className={`flex size-8 items-center justify-center rounded-full ${
                          isStepSkipped
                            ? 'bg-gray-200 text-gray-500'
                            : isActive || isCompleted
                              ? 'bg-blue-600 text-white'
                              : 'border-2 border-gray-300 bg-white text-gray-500'
                        }`}
                      >
                        {isStepSkipped ? (
                          <span className="text-sm font-medium">−</span>
                        ) : isCompleted ? (
                          <svg
                            className="size-5"
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        ) : (
                          <span className="text-sm font-medium">{step.id}</span>
                        )}
                      </div>
                      <div className="mt-2 text-center">
                        <p
                          className={`text-sm font-medium ${
                            isStepSkipped
                              ? 'text-gray-400'
                              : isActive || isCompleted
                                ? 'text-blue-600'
                                : 'text-gray-500'
                          }`}
                        >
                          {isStepSkipped ? 'Skipped' : step.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {isStepSkipped ? 'Using existing role' : step.description}
                        </p>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          </nav>
        </div>
      </div>

      {/* Body */}
      <div className="px-4 py-6 sm:px-6 lg:px-8 max-h-[60vh] overflow-y-auto">
        {getStepContent()}
      </div>

      {/* Footer */}
      <div className="px-4 py-4 sm:px-6 lg:px-8 border-t border-gray-200 bg-gray-50">
        <div className="flex justify-between">
          <button
            type="button"
            onClick={currentStep === 1 ? handleClose : prevStep}
            className="py-2.5 px-4 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-800 shadow-xs hover:bg-gray-50 focus:outline-hidden focus:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none"
          >
            {currentStep === 1 ? 'Cancel' : 'Previous'}
          </button>

          <button
            type="button"
            onClick={currentStep === (isAdmin && isAdminView ? 4 : 3) ? addApplication : nextStep}
            disabled={(currentStep === 1 && !canProceedToNext()) || isSubmitting}
            className="py-2.5 px-4 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg border border-transparent bg-blue-600 text-white hover:bg-blue-700 focus:outline-hidden focus:bg-blue-700 disabled:opacity-50 disabled:pointer-events-none"
          >
            {isSubmitting ? (
              <>
                <div
                  className="animate-spin inline-block size-4 border-[3px] border-current border-t-transparent text-white rounded-full"
                  role="status"
                  aria-label="loading"
                >
                  <span className="sr-only">Loading...</span>
                </div>
                Creating...
              </>
            ) : currentStep === (isAdmin && isAdminView ? 4 : 3) ? (
              'Add Application to Tracker'
            ) : (
              'Next'
            )}
          </button>
        </div>
      </div>

      {/* Company Search Modal */}
      <CompanySearchModal
        isOpen={companySearchModalOpen}
        onClose={() => setCompanySearchModalOpen(false)}
        onSelectCompany={handleCompanySelect}
        currentCompany={applicationInfo.companyName}
      />
    </div>
  );

  if (showOverlay) {
    return (
      <div className="fixed inset-0 z-9999 overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <div
            className="fixed inset-0 bg-gray-500/75 transition-opacity"
            onClick={handleClose}
          ></div>
          {modalContent}
        </div>
      </div>
    );
  }

  return modalContent;
};

export default AddApplicationModal;
